import { useEffect, useMemo } from 'react';
import Iconify from 'src/components/Iconify';
import { Autocomplete, Dialog, Grid, IconButton, Stack, TextField } from '@mui/material';
import { i18nFunction } from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { TaskOutput } from 'src/careflow/ph-core/fhir-client/resources';
import { ErrorFieldState } from './TaskModal';
import { GridSection } from 'src/components/CustomModal';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import {
  canViewDocument,
  SelectAttachmentState,
  VALUE_KEYS,
  ValueKey,
} from 'src/careflow/common/document/document';
import { fhirClient } from 'src/App';
import { FileUpload } from '@mui/icons-material';
import useObjectState from 'src/hooks/useObjectState';
import { TaskDocument } from './TaskDocument';
import Viewer from 'src/careflow/care-coordination/patient/assessment/viewer/Viewer';
import { WrappedDocumentReference } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/DocumentReference';

type Props = {
  task: WrappedTask | undefined;
  taskOutput: TaskOutput[] | null;
  patient: WrappedPatient | null;
  updateState: Function;
  errors: ErrorFieldState;
  updateErrorState: Function;
  i18n: i18nFunction;
  configurations: any;
};

const TaskOutputComponent = ({
  task,
  taskOutput,
  patient,
  errors,
  updateState,
  updateErrorState,
  i18n,
  configurations,
}: Props) => {
  const [{ openUploadFile, index, selectedDocumentReference }, updateSelectAttachmentState] =
    useObjectState<SelectAttachmentState>({
      openUploadFile: false,
      index: null,
      selectedDocumentReference: null,
    });

  useEffect(() => {
    if (taskOutput === undefined) {
      updateState({
        taskOutputs: [{ type: {}, valueString: '' }],
      });
    }
  }, [taskOutput]);

  const {
    valueSets: [taskOutputTypeVS],
    isLoading: isFetchingVS,
  } = useValueSetsByIdentifiers(['ph-task-output-types']);
  const { fhir, outputTypeFiles } = configurations ?? {};

  const handleAddOutput = () => {
    updateState({
      taskOutputs: [...(taskOutput ?? []), { type: {}, valueString: '' }],
    });
  };

  const outputTypeCodeableConcept = useMemo(
    () =>
      (taskOutputTypeVS?.asList?.() ?? []).map((concept) => ({
        coding: [
          {
            code: concept?.code,
            display: concept?.display,
            system: taskOutputTypeVS?.getConceptSystem?.(),
          },
        ],
      })),
    [taskOutputTypeVS]
  );

  const displayOutputs =
    taskOutput && Array.isArray(taskOutput) && taskOutput.length > 0
      ? taskOutput
      : [{ type: {}, valueString: '' }];

  const getCurrentOutputs = () =>
    taskOutput && Array.isArray(taskOutput) && taskOutput.length > 0
      ? taskOutput.map((item) => ({
          ...(item ?? {}),
          type: item?.type ? { ...item.type } : item?.type,
        }))
      : [{ type: {}, valueString: '' }];

  const canEditOutput = checkAclValidation({
    acls: [crsAcls.CRS.PATIENT.TASKS.INPUT_OUTPUT.EDIT, crsAcls.CRS.CASE.TASKS.INPUT_OUTPUT.EDIT],
  });

  const canDeleteOutput = checkAclValidation({
    acls: [
      crsAcls.CRS.PATIENT.TASKS.INPUT_OUTPUT.DELETE,
      crsAcls.CRS.CASE.TASKS.INPUT_OUTPUT.DELETE,
    ],
  });

  const handleTaskOutputChange = (index: number, field: 'type' | 'value', value: any) => {
    if (!canEditOutput) return;

    const updatedOutputs = getCurrentOutputs();

    if (!updatedOutputs[index]) {
      updatedOutputs[index] = { type: {} };
    }

    if (field === 'type') {
      const outputType = value?.coding?.[0]?.code;
      const currentOutput = { ...updatedOutputs[index], type: value };

      if (outputType) {
        VALUE_KEYS.forEach((key) => {
          delete currentOutput[key];
        });
      }

      updatedOutputs[index] = currentOutput;

      if (value) {
        updateErrorState({ taskOutputs: null });
      }
    }

    if (field === 'value') {
      const outputType = updatedOutputs[index]?.type?.coding?.[0]?.code;

      if (!outputType) {
        updatedOutputs[index] = {
          ...updatedOutputs[index],
          valueString: value,
        };
        updateState({ taskOutputs: updatedOutputs });
        return;
      }

      const config = outputTypeFiles[outputType];
      const valueKey: ValueKey = config?.valueType ?? 'valueString';
      const currentOutput = { ...updatedOutputs[index] };

      VALUE_KEYS.forEach((key) => {
        delete currentOutput[key];
      });

      updatedOutputs[index] = {
        ...currentOutput,
        [valueKey]: value,
      };
    }

    updateState({ taskOutputs: updatedOutputs });
  };

  const handlePreview = async (outputValue: any) => {
    updateSelectAttachmentState({ selectedDocumentReference: null });
    if (!outputValue) return;

    if (outputValue.startsWith('http')) {
      window.open(outputValue, '_blank');
      return;
    }

    if (outputValue.includes('DocumentReference')) {
      const id = outputValue.split('/')[1];

      const res = await fhirClient.findById('DocumentReference', id);
      const data = res as WrappedDocumentReference;

      if (data?.id) {
        updateSelectAttachmentState({ selectedDocumentReference: data });
      }
    }
  };

  const exists = (index, value) =>
    errors?.taskOutputs?.some(
      (item) => item?.index === index && item?.invalidFields?.includes(value)
    );

  return (
    <Stack direction="column" mt={2} ml={2} mb={2}>
      {displayOutputs.map((item: any, index) => {
        const outputValue =
          item?.valueString ?? item?.valueUrl ?? item?.valueReference?.reference ?? '';
        const outputType = item?.type?.coding?.[0]?.code ?? '';

        const { canPreviewDocumentReference, canPreviewUrl, canUpload, isFile } = canViewDocument(
          outputTypeFiles,
          outputType,
          outputValue
        );
        return (
          <GridSection container spacing={2} key={`output-${index}`} mt={index === 0 ? -2 : 0.4}>
            <Grid item xs={4}>
              <Autocomplete
                disabled={isFetchingVS || !canEditOutput || canPreviewDocumentReference}
                value={item.type ?? undefined}
                fullWidth
                onChange={(_, newValue) => {
                  handleTaskOutputChange(index, 'type', newValue);
                }}
                options={outputTypeCodeableConcept}
                getOptionLabel={(option: any) =>
                  option?.coding?.[0]?.display ?? option?.coding?.[0]?.code ?? ''
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={i18n('patients.details.tasks.outputType', 'crs')}
                    variant="outlined"
                    error={exists(index, 'type')}
                    helperText={exists(index, 'type') && 'Must select a valid output type'}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6.6}>
              <TextField
                fullWidth
                disabled={isFetchingVS || !canEditOutput || canPreviewDocumentReference || isFile}
                value={outputValue}
                onChange={(e) => {
                  handleTaskOutputChange(index, 'value', e.target.value);
                }}
                label={i18n('patients.details.tasks.output', 'crs')}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={1.4}>
              {canUpload && (
                <IconButton
                  onClick={() =>
                    updateSelectAttachmentState({ openUploadFile: true, index: index })
                  }
                  size="medium"
                  sx={{ '&:hover': { background: 'none' } }}
                  disabled={isFetchingVS || !canEditOutput}
                >
                  <FileUpload />
                </IconButton>
              )}

              {(canPreviewUrl || canPreviewDocumentReference) && (
                <IconButton
                  onClick={() => handlePreview(outputValue)}
                  size="medium"
                  sx={{ '&:hover': { background: 'none' } }}
                >
                  <Iconify icon="eva:eye-outline" />
                </IconButton>
              )}
              <IconButton
                onClick={handleAddOutput}
                size="medium"
                sx={{ '&:hover': { background: 'none' } }}
                disabled={isFetchingVS || !canEditOutput}
              >
                <Iconify icon="eva:plus-fill" />
              </IconButton>

              <IconButton
                onClick={(e) => {
                  if (!canDeleteOutput) return;
                  e.stopPropagation();
                  e.preventDefault();
                  const currentOutputs =
                    taskOutput && Array.isArray(taskOutput) && taskOutput.length > 0
                      ? [...taskOutput]
                      : [];
                  if (currentOutputs.length === 0) return;
                  const updated = currentOutputs.filter((_, i) => i !== index);
                  updateState({ taskOutputs: updated.length > 0 ? updated : null });
                  if (errors?.taskOutputs) {
                    const newErrors = errors.taskOutputs.filter((_, i) => i !== index);
                    updateErrorState({
                      taskOutputs: newErrors.length > 0 ? newErrors : null,
                    });
                  }
                }}
                size="medium"
                sx={{ '&:hover': { background: 'none' } }}
                disabled={isFetchingVS || !canDeleteOutput}
              >
                <Iconify icon="eva:trash-2-outline" />
              </IconButton>
            </Grid>
          </GridSection>
        );
      })}
      <TaskDocument
        open={openUploadFile}
        setOpen={() =>
          updateSelectAttachmentState({
            openUploadFile: false,
            index: null,
            selectedDocumentReference: null,
          })
        }
        task={task}
        patient={patient}
        configurations={fhir}
        i18n={i18n}
        updateState={(value) => {
          if (index !== null) {
            handleTaskOutputChange(index, 'value', value);
          }
        }}
      />
      {selectedDocumentReference && (
        <Dialog
          fullWidth
          maxWidth="md"
          open={selectedDocumentReference !== null}
          onClose={() => updateSelectAttachmentState({ selectedDocumentReference: null })}
        >
          <Viewer
            documentReference={selectedDocumentReference}
            typeResource="documentReference"
            patientId={patient?.id}
          />
        </Dialog>
      )}
    </Stack>
  );
};

export default TaskOutputComponent;
